/**
 * @license
 *-------------------------------------------------------------------------------------------
 * Copyright © 2024 Progress Software Corporation. All rights reserved.
 * Licensed under commercial license. See LICENSE.md in the package root for more information
 *-------------------------------------------------------------------------------------------
 */
"use client";
import * as a from "react";
import l from "prop-types";
import { ChipListSelectionContext as H, ChipListFocusContext as U, ChipListDataContext as W } from "./chip-list-contexts.mjs";
import { validatePackage as j, useDir as q, Keys as u, useMouse as G, getTabIndex as J, classNames as T, kendoThemeMaps as O, IconWrap as I, toIconName as C, svgIconPropType as E } from "@progress/kendo-react-common";
import { xCircleIcon as w } from "@progress/kendo-svg-icons";
import { FOCUS_ACTION as v } from "./focus-reducer.mjs";
import { CHIP_DATA_ACTION as Q } from "./data-reducer.mjs";
import { SELECTION_ACTION as x } from "./selection-reducer.mjs";
import { packageMetadata as V } from "../package-metadata.mjs";
const z = a.forwardRef((e, A) => {
  j(V);
  const c = a.useRef(null), d = a.useRef(null), {
    disabled: y = n.disabled,
    fillMode: r = n.fillMode,
    themeColor: N = n.themeColor,
    size: f = n.size,
    rounded: k = n.rounded,
    dir: B = n.dir,
    removeIcon: D = n.removeIcon,
    removeSvgIcon: F = n.removeSvgIcon,
    removable: s = n.removable
  } = e, M = q(d, B);
  a.useImperativeHandle(c, () => ({
    element: d.current,
    props: e
  })), a.useImperativeHandle(A, () => c.current);
  const [m, i] = a.useContext(H), [R, o] = a.useContext(U), [, S] = a.useContext(W), h = a.useMemo(
    () => e.selected || (Array.isArray(m) ? m.some((t) => t === e.value) : m === e.value),
    [e.selected, e.value, m]
  ), g = a.useMemo(
    () => R === e.value,
    [e.value, R]
  );
  a.useEffect(
    () => {
      g && d.current && d.current.focus();
    },
    [g]
  );
  const K = a.useCallback(
    (t) => {
      i({ type: x.toggle, payload: e.value, event: t });
    },
    [i, e.value]
  ), b = a.useCallback(
    (t) => {
      s && (S({ type: Q.remove, payload: e.value, event: t }), o({ type: v.reset, payload: e.value, event: t }), i({ type: x.remove, payload: e.value, event: t }), e.onRemove && e.onRemove.call(
        void 0,
        {
          target: c.current,
          syntheticEvent: t
        }
      ));
    },
    [e.onRemove, e.value, s, S, o, i]
  ), P = a.useCallback(
    (t) => {
      switch (t.keyCode) {
        case u.left:
          o({ type: v.prev, payload: e.value, event: t });
          break;
        case u.right:
          o({ type: v.next, payload: e.value, event: t });
          break;
        case u.enter:
          i({ type: x.toggle, payload: e.value, event: t });
          break;
        case u.delete:
          b(t);
          break;
      }
      e.onKeyDown && e.onKeyDown.call(
        void 0,
        {
          target: c.current,
          syntheticEvent: t
        }
      );
    },
    [e.onKeyDown, e.value, o, i, b]
  ), $ = a.useCallback(
    (t) => {
      o({ payload: e.value, type: v.current, event: t }), e.onFocus && e.onFocus.call(
        void 0,
        {
          target: c.current,
          syntheticEvent: t
        }
      );
    },
    [e.onFocus, e.value, o]
  ), L = a.useCallback(
    (t) => {
      e.onBlur && e.onBlur.call(
        void 0,
        {
          target: c.current,
          syntheticEvent: t
        }
      );
    },
    [e.onBlur]
  ), _ = G(e, c, { onClick: K });
  return /* @__PURE__ */ a.createElement(
    "div",
    {
      ..._,
      role: e.role || "button",
      id: e.value,
      style: e.style,
      ref: d,
      dir: M,
      tabIndex: J(e.tabIndex, y, void 0),
      className: T(
        "k-chip",
        {
          "k-rtl": M === "rtl",
          "k-disabled": y,
          "k-selected": h,
          "k-focus": g,
          [`k-chip-${O.sizeMap[f] || f}`]: f,
          [`k-rounded-${O.roundedMap[k] || k}`]: k,
          [`k-chip-${r}`]: r,
          [`k-chip-${r}-${N}`]: !!(r && N)
        },
        e.className
      ),
      "aria-pressed": e.role ? void 0 : h,
      "aria-disabled": y,
      "aria-describedby": e.ariaDescribedBy,
      "aria-keyshortcuts": s ? "Enter Delete" : void 0,
      onFocus: $,
      onBlur: L,
      onKeyDown: P
    },
    h && (e.selectedIcon || e.selectedSvgIcon) && /* @__PURE__ */ a.createElement(
      I,
      {
        className: "k-chip-icon",
        name: e.selectedIcon ? C(e.selectedIcon) : void 0,
        icon: e.selectedSvgIcon,
        size: "small"
      }
    ),
    (e.icon || e.svgIcon) && /* @__PURE__ */ a.createElement(
      I,
      {
        className: "k-chip-icon",
        name: e.icon ? C(e.icon) : void 0,
        icon: e.svgIcon,
        size: "small"
      }
    ),
    e.avatar && /* @__PURE__ */ a.createElement(
      "div",
      {
        className: `k-chip-avatar k-avatar k-rounded-${e.avatar.rounded} k-avatar-md k-avatar-solid k-avatar-solid-primary`,
        style: e.avatar.style
      },
      /* @__PURE__ */ a.createElement("span", { className: "k-avatar-image" }, /* @__PURE__ */ a.createElement(
        "img",
        {
          src: e.avatar.image,
          alt: e.avatar.imageAlt
        }
      ))
    ),
    /* @__PURE__ */ a.createElement("span", { className: "k-chip-content" }, e.children !== void 0 ? e.children : e.text && /* @__PURE__ */ a.createElement(
      "span",
      {
        "aria-label": e.ariaLabel || e.text,
        className: "k-chip-label"
      },
      e.text
    )),
    s && /* @__PURE__ */ a.createElement("span", { className: "k-chip-actions" }, /* @__PURE__ */ a.createElement("span", { className: T("k-chip-action", "k-chip-remove-action"), onClick: b }, /* @__PURE__ */ a.createElement(
      I,
      {
        name: D ? C(D) : void 0,
        icon: F || w,
        size: "small"
      }
    )))
  );
}), X = {
  id: l.string,
  text: l.string,
  value: l.any,
  dir: l.oneOf(["ltr", "rtl"]),
  removable: l.bool,
  removeIcon: l.string,
  removeIconSvg: E,
  disabled: l.bool,
  icon: l.string,
  svgIcon: E,
  selectedIcon: l.string,
  selectedIconSvg: E,
  onRemove: l.func,
  dataItem: l.any,
  selected: l.bool,
  ariaDescribedBy: l.string,
  size: l.oneOf([null, "small", "medium", "large"]),
  rounded: l.oneOf([null, "small", "medium", "large", "full"]),
  fillMode: l.oneOf([null, "outline", "solid"]),
  // eslint-disable-next-line max-len
  themeColor: l.oneOf([null, "base", "info", "success", "warning", "error"])
}, n = {
  disabled: !1,
  removable: !1,
  removeIcon: "k-i-x-circle",
  removeSvgIcon: w,
  dir: "ltr",
  size: "medium",
  rounded: "medium",
  fillMode: "solid",
  themeColor: "base"
};
z.displayName = "KendoReactChip";
z.propTypes = X;
export {
  z as Chip
};
